import {
  useCreateCommentMutation,
  useThreadFileGetURLQueryLazyQuery,
  useThreadFileUploadURLQueryLazyQuery,
  Comment,
  useUpdateCommentMutation
} from 'graphql/generated'
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import dynamic from 'next/dynamic'
import { useControlledInput } from 'hooks'
import Button from 'components/Button'
import { useForumContext } from './ForumContext'
import { noop } from 'lodash'
import { Typography } from 'components/common'
import ReactQuill from 'react-quill'
import { IProps } from 'components/QuillNoSSR'
import useS3FileUpload from './useS3FileUpload'
import convertImageUrlsToS3Key from './convertImageUrlsToS3Key'
import useConvertS3KeysToImageUrl from './useConvertS3KeysToImageUrls'
import { useAnalyticsContext } from 'context/AnalyticsContext'

type TWrappedComponent = {
  forwardedRef: RefObject<ReactQuill>
} & IProps

const QuillNoSSRWrapper = dynamic(
  async () => {
    const { default: Quill } = await import('components/QuillNoSSR')
    return ({ forwardedRef, ...props }: TWrappedComponent) => {
      const quill = useMemo(() => {
        return <Quill ref={forwardedRef} {...props} advancedButtons />
      }, [forwardedRef, props])
      return quill
    }
  },
  { ssr: false }
)

const CommentInput: React.FC<{
  parentId: string
  comment?: Pick<Comment, 'body' | 'attachments' | 'id'>
  onSave: Callback
  onCancel?: Callback
  placeHolder?: string
  saveButtonLabel?: string
  type: 'Comment' | 'Reply'
}> = ({
  parentId,
  comment,
  onSave,
  onCancel,
  placeHolder = 'Leave a comment',
  saveButtonLabel = 'Comment',
  type
}) => {
  const { selectedCategory, discussionBoard } = useForumContext()
  const updateUrls = useConvertS3KeysToImageUrl(discussionBoard.id)

  const [body, setBody] = useControlledInput()
  const { loaded: analyticsLoaded } = useAnalyticsContext()

  useEffect(() => {
    async function init() {
      if (comment) {
        setBody(await updateUrls(comment.body))
      }
    }
    init()
  }, [comment, setBody, updateUrls])

  const [createComment, { loading: isCreating }] = useCreateCommentMutation()

  const [updateComment, { loading: isUpdating }] = useUpdateCommentMutation()
  const cantSave = !body.trim().length || body === '<p><br></p>'

  const saveComment = useCallback(async () => {
    if (cantSave) {
      return
    }
    if (!comment) {
      await createComment({
        variables: {
          threadId: parentId,
          commentDetails: {
            body: convertImageUrlsToS3Key(body),
            attachments: []
          }
        },

        onCompleted: () => {
          if (analyticsLoaded) {
            window.analytics?.trackAll(`Forum - ${type} - Created`, {
              threadId: parentId
            })
          }
          onSave()
        }
      })
    } else {
      await updateComment({
        variables: {
          threadId: comment.id,
          commentDetails: {
            body: convertImageUrlsToS3Key(body),
            newAttachments: [],
            existingAttachments: []
          }
        },

        onCompleted: () => {
          if (analyticsLoaded) {
            window.analytics?.trackAll(`Forum - ${type} - Edit`, {
              commentId: comment.id,
              threadId: parentId
            })
          }
          onSave()
        }
      })
    }

    setBody('')
  }, [
    cantSave,
    comment,
    setBody,
    createComment,
    parentId,
    body,
    analyticsLoaded,
    onSave,
    type,
    updateComment
  ])

  const canComment = !selectedCategory.archived

  const quillRef = useRef<ReactQuill>(null)
  const [uploadingImage, setUploadingImage] = useState<boolean>(false)
  const [getUploadUrl] = useThreadFileUploadURLQueryLazyQuery()
  const [getFileUrl] = useThreadFileGetURLQueryLazyQuery()
  const { upload, uploadStatus } = useS3FileUpload()

  const imageUpload = useCallback(
    async (file: File) => {
      setUploadingImage(true)
      const editor = quillRef.current?.getEditor()
      if (!editor) {
        return
      }
      const embedIndex = editor.getSelection()?.index || 0
      const { data: uploadUrl } = await getUploadUrl({
        variables: {
          discussionBoardId: discussionBoard.id,
          fileName: encodeURIComponent(file.name.trim()).replaceAll(
            /%../gm,
            '-'
          ),
          fileType: file.type
        }
      })

      if (!uploadUrl) {
        return
      }
      const key = uploadUrl.threadFileUploadUrl.key
      await upload(file, uploadUrl.threadFileUploadUrl.url, key)

      const { data } = await getFileUrl({
        variables: {
          discussionBoardId: discussionBoard.id,
          key
        }
      })

      editor.insertEmbed(embedIndex, 'image', data?.threadFileGetUrl.url)
      setUploadingImage(false)

      if (analyticsLoaded) {
        window.analytics?.trackAll(`Forum - Comment - Image Upload`, {
          parentId: parentId,
          key
        })
      }
    },
    [
      analyticsLoaded,
      discussionBoard.id,
      getFileUrl,
      getUploadUrl,
      parentId,
      upload
    ]
  )

  return (
    <div className="relative">
      <QuillNoSSRWrapper
        forwardedRef={quillRef}
        detailsHtml={body}
        setDetailsHtml={canComment ? setBody : noop}
        placeholder={placeHolder}
        className="focus-within:border-blue-500 forum-comment-quill"
        imageHandler={() => {
          const input = document.createElement('input')

          input.setAttribute('type', 'file')
          input.setAttribute('accept', 'image/*')
          input.click()

          input.onchange = async () => {
            if (!input.files) {
              return
            }
            const file: any = input.files[0]
            await imageUpload(file)
          }
        }}
      />

      <div className="absolute flex gap-2 right-2 bottom-1.5">
        {onCancel && (
          <Button
            color="white"
            label="Cancel"
            onClick={onCancel}
            size="xs"
            disabled={isCreating || isUpdating}
          />
        )}

        <Button
          label={saveButtonLabel}
          onClick={saveComment}
          size="xs"
          loading={isCreating || isUpdating}
          disabled={isCreating || isUpdating || cantSave}
        />
      </div>

      {!canComment && (
        <div className="absolute w-full h-full inset-0 flex flex-col items-center justify-center bg-gray-100/70">
          <Typography color="darkGray">{`This category is archived, edit and comments are disabled.`}</Typography>
        </div>
      )}

      {uploadingImage && (
        <div className="absolute w-full h-full inset-0 flex flex-col px-4 justify-center items-center bg-gray-900/70">
          <div className="text-center text-sm text-white">
            Uploading {uploadStatus.name}
          </div>

          <div className="w-full rounded-full bg-gray-700 mt-4">
            <div
              className="bg-blue-600 h-2.5 rounded-full"
              style={{
                width: `${uploadStatus.progress}%`
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default CommentInput
